import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch, getUserFacingErrorMessage } from '../services/apiClient';
import './RunEvaluation.css';

const RunEvaluation = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    agent_name: 'extractor',
    eval_id: 'resume_extraction_eval.v1',
    model_name: 'gpt-4o-mini',
    temperature: 0.0,
    max_samples: 10
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const agents = [
    { value: 'extractor', label: 'Extractor', eval_id: 'resume_extraction_eval.v1' },
    { value: 'evaluator', label: 'Evaluator', eval_id: 'resume_evaluation_eval.v1' },
    { value: 'summarizer', label: 'Summarizer', eval_id: 'resume_summarization_eval.v1' },
    { value: 'formatter', label: 'Formatter', eval_id: 'resume_formatting_eval.v1' },
    { value: 'explainability', label: 'Explainability', eval_id: 'resume_explainability_eval.v1' }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      // Auto-update eval_id when agent changes
      if (name === 'agent_name') {
        const agent = agents.find(a => a.value === value);
        if (agent) {
          updated.eval_id = agent.eval_id;
        }
      }
      return updated;
    });
    setError(null);
    setSuccess(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await apiFetch('/api/evals/run', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          eval_id: formData.eval_id,
          agent_name: formData.agent_name,
          configuration: {
            model_name: formData.model_name,
            temperature: parseFloat(formData.temperature),
            max_samples: parseInt(formData.max_samples, 10)
          }
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(getUserFacingErrorMessage(response.status, data, 'Failed to start evaluation'));
      }

      setSuccess(`Evaluation started successfully! Run ID: ${data.run_id}`);
      
      // Redirect to results after a short delay
      setTimeout(() => {
        navigate(`/evals/results/${data.run_id}`);
      }, 2000);
    } catch (err) {
      setError(err.message || 'Failed to start evaluation');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="run-evaluation">
      <div className="run-evaluation-header">
        <h1>Run Agent Evaluation</h1>
        <button 
          className="btn-secondary" 
          onClick={() => navigate('/evals')}
        >
          ← Back to Dashboard
        </button>
      </div>

      <form onSubmit={handleSubmit} className="evaluation-form">
        <div className="form-group">
          <label htmlFor="agent_name">Agent *</label>
          <select
            id="agent_name"
            name="agent_name"
            value={formData.agent_name}
            onChange={handleChange}
            required
          >
            {agents.map(agent => (
              <option key={agent.value} value={agent.value}>
                {agent.label}
              </option>
            ))}
          </select>
          <small>Select the agent to evaluate</small>
        </div>

        <div className="form-group">
          <label htmlFor="eval_id">Evaluation ID *</label>
          <input
            type="text"
            id="eval_id"
            name="eval_id"
            value={formData.eval_id}
            onChange={handleChange}
            required
          />
          <small>The evaluation configuration identifier</small>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="model_name">Model Name</label>
            <input
              type="text"
              id="model_name"
              name="model_name"
              value={formData.model_name}
              onChange={handleChange}
            />
            <small>OpenAI model to use (e.g., gpt-4o-mini)</small>
          </div>

          <div className="form-group">
            <label htmlFor="temperature">Temperature</label>
            <input
              type="number"
              id="temperature"
              name="temperature"
              value={formData.temperature}
              onChange={handleChange}
              min="0"
              max="2"
              step="0.1"
            />
            <small>Model temperature (0.0 = deterministic)</small>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="max_samples">Max Samples</label>
          <input
            type="number"
            id="max_samples"
            name="max_samples"
            value={formData.max_samples}
            onChange={handleChange}
            min="1"
            max="100"
          />
          <small>Maximum number of test samples to evaluate</small>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {success && (
          <div className="success-message">
            {success}
          </div>
        )}

        <div className="form-actions">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => navigate('/evals')}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={loading}
          >
            {loading ? 'Starting...' : 'Start Evaluation'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default RunEvaluation;



